import { create } from "zustand";
import { getDashboardAnalytics, getPartnerAnalytics, getTransactionAnalytics, getChargesSummary, exportChargesData } from "../data/api/analytics";
import { processInternalSettlement, getSettlementHistory, getSettlementsByPartner, getSettlementsByDate, getSettlementStats, getPartnerList, getPartnerBalance } from "../data/api/internalSettlement";

export const useAnalyticsStore = create((set, get) => ({
  // Dashboard Analytics State
  dashboardData: null,
  dashboardLoading: false,
  dashboardError: null,

  // Partner Analytics State
  partnerData: null,
  partnerLoading: false,
  partnerError: null,

  // Transaction Analytics State
  transactionData: null,
  transactionLoading: false,
  transactionError: null,

  // Charges Analytics State
  chargesData: null,
  chargesLoading: false,
  chargesError: null,

  // Internal Settlement State
  settlementData: null,
  settlementLoading: false,
  settlementError: null,
  settlementHistory: null,
  settlementHistoryLoading: false,
  settlementHistoryError: null,
  settlementStats: null,
  settlementStatsLoading: false,
  settlementStatsError: null,
  partnerList: null,
  partnerListLoading: false,
  partnerListError: null,
  selectedPartnerBalance: null,
  partnerBalanceLoading: false,
  partnerBalanceError: null,

  // Date Range State
  selectedPeriod: 30,
  selectedDateRange: {
    start: null,
    end: null,
  },

  // Actions
  setSelectedPeriod: (period) => set({ selectedPeriod: period }),
  setSelectedDateRange: (dateRange) => set({ selectedDateRange: dateRange }),

  // Dashboard Analytics Actions
  fetchDashboardAnalytics: async () => {
    set({ dashboardLoading: true, dashboardError: null });
    try {
      const data = await getDashboardAnalytics();
      set({ dashboardData: data, dashboardLoading: false });
      return data;
    } catch (error) {
      set({ 
        dashboardError: error.message, 
        dashboardLoading: false 
      });
      throw error;
    }
  },

  // Partner Analytics Actions
  fetchPartnerAnalytics: async (partnerId) => {
    set({ partnerLoading: true, partnerError: null });
    try {
      const data = await getPartnerAnalytics(partnerId);
      set({ partnerData: data, partnerLoading: false });
      return data;
    } catch (error) {
      set({ 
        partnerError: error.message, 
        partnerLoading: false 
      });
      throw error;
    }
  },

  // Transaction Analytics Actions
  fetchTransactionAnalytics: async (period = 30) => {
    set({ transactionLoading: true, transactionError: null });
    try {
      const data = await getTransactionAnalytics(period);
      set({ transactionData: data, transactionLoading: false });
      return data;
    } catch (error) {
      set({ 
        transactionError: error.message, 
        transactionLoading: false 
      });
      throw error;
    }
  },

  // Charges Analytics Actions
  fetchChargesSummary: async (startDate = null, endDate = null, partnerId = null, page = 1, perPage = 50) => {
    set({ chargesLoading: true, chargesError: null });
    try {
      const data = await getChargesSummary(startDate, endDate, partnerId, page, perPage);
      set({ chargesData: data, chargesLoading: false });
      return data;
    } catch (error) {
      // console.error("❌ Analytics store: Error in fetchChargesSummary:", error);
      set({ 
        chargesError: error.message, 
        chargesLoading: false 
      });
      throw error;
    }
  },

  exportChargesData: async (startDate = null, endDate = null, partnerId = null) => {
    try {
      const result = await exportChargesData(startDate, endDate, partnerId);
      return result;
    } catch (error) {
      throw error;
    }
  },

  // Internal Settlement Actions
  processInternalSettlement: async (partnerId, amount, description) => {
    set({ settlementLoading: true, settlementError: null });
    try {
      const data = await processInternalSettlement(partnerId, amount, description);
      set({ settlementData: data, settlementLoading: false });
      return data;
    } catch (error) {
      set({ 
        settlementError: error.message, 
        settlementLoading: false 
      });
      throw error;
    }
  },

  fetchSettlementHistory: async (partnerId = null, page = 1, perPage = 50, startDate = null, endDate = null, status = null) => {
    set({ settlementHistoryLoading: true, settlementHistoryError: null });
    try {
      const data = await getSettlementHistory(partnerId, page, perPage, startDate, endDate, status);
      set({ settlementHistory: data, settlementHistoryLoading: false });
      return data;
    } catch (error) {
      set({ 
        settlementHistoryError: error.message, 
        settlementHistoryLoading: false 
      });
      throw error;
    }
  },

  fetchSettlementsByPartner: async (partnerId, startDate = null, endDate = null, status = null, perPage = 50) => {
    set({ settlementHistoryLoading: true, settlementHistoryError: null });
    try {
      const data = await getSettlementsByPartner(partnerId, startDate, endDate, status, perPage);
      set({ settlementHistory: data, settlementHistoryLoading: false });
      return data;
    } catch (error) {
      set({ 
        settlementHistoryError: error.message, 
        settlementHistoryLoading: false 
      });
      throw error;
    }
  },

  fetchSettlementsByDate: async (startDate, endDate, partnerId = null, status = null, perPage = 50) => {
    set({ settlementHistoryLoading: true, settlementHistoryError: null });
    try {
      const data = await getSettlementsByDate(startDate, endDate, partnerId, status, perPage);
      set({ settlementHistory: data, settlementHistoryLoading: false });
      return data;
    } catch (error) {
      set({ 
        settlementHistoryError: error.message, 
        settlementHistoryLoading: false 
      });
      throw error;
    }
  },

  fetchSettlementStats: async (startDate = null, endDate = null) => {
    set({ settlementStatsLoading: true, settlementStatsError: null });
    try {
      const data = await getSettlementStats(startDate, endDate);
      set({ settlementStats: data, settlementStatsLoading: false });
      return data;
    } catch (error) {
      set({ 
        settlementStatsError: error.message, 
        settlementStatsLoading: false 
      });
      throw error;
    }
  },

  fetchPartnerList: async () => {
    set({ partnerListLoading: true, partnerListError: null });
    try {
      const data = await getPartnerList();
      set({ partnerList: data, partnerListLoading: false });
      return data;
    } catch (error) {
      set({ 
        partnerListError: error.message, 
        partnerListLoading: false 
      });
      throw error;
    }
  },

  fetchPartnerBalance: async (partnerId) => {
    set({ partnerBalanceLoading: true, partnerBalanceError: null });
    try {
      const data = await getPartnerBalance(partnerId);
      set({ selectedPartnerBalance: data, partnerBalanceLoading: false });
      return data;
    } catch (error) {
      set({ 
        partnerBalanceError: error.message, 
        partnerBalanceLoading: false 
      });
      throw error;
    }
  },

  // Clear States
  clearDashboardData: () => set({ dashboardData: null, dashboardError: null }),
  clearPartnerData: () => set({ partnerData: null, partnerError: null }),
  clearTransactionData: () => set({ transactionData: null, transactionError: null }),
  clearChargesData: () => set({ chargesData: null, chargesError: null }),
  clearSettlementData: () => set({ 
    settlementData: null, 
    settlementError: null,
    settlementHistory: null,
    settlementHistoryError: null,
    selectedPartnerBalance: null,
    partnerBalanceError: null
  }),
  clearAllAnalytics: () => set({
    dashboardData: null,
    dashboardError: null,
    partnerData: null,
    partnerError: null,
    transactionData: null,
    transactionError: null,
    chargesData: null,
    chargesError: null,
    settlementData: null,
    settlementError: null,
    settlementHistory: null,
    settlementHistoryError: null,
    selectedPartnerBalance: null,
    partnerBalanceError: null,
  }),
})); 